import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { HiChevronDown, HiChevronUp, HiMagnifyingGlass, HiPlus, HiXMark } from "react-icons/hi2";
import { IoInformationCircleOutline } from "react-icons/io5";
import { FaRobot } from "react-icons/fa";

import IaTool from "./IaTool";
import Comments from "./Comments";
import MultiSelect from "../../../../../../components/MultiSelect";
import SelectUser2 from "../../../../../../components/SelectUser2";
import api from "../../../../../../services/api";
import { useLocation } from "react-router-dom";

const Info = ({ item, setItem, project, onUpdate }) => {
  const [users, setUsers] = useState([]);
  const [isIaToolOpen, setIsIaToolOpen] = useState(false);
  const [iaResponse, setIaResponse] = useState("");
  const { pathname } = useLocation();

  const isFromBacklog = pathname.includes("/backlog");

  async function getUsers() {
    try {
      const { ok, data } = await api.post(`/user/search`, { project_ids: [project._id], status: "active" });
      if (!ok) throw new Error(data.error);
      setUsers(data.users);
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    getUsers();
  }, []);

  const updateItem = async (updatedFields) => {
    const originalItem = { ...item };
    // immediately update UI
    setItem({ ...item, ...updatedFields });

    // Then process the API call in the background
    try {
      const { ok, data } = await api.put(`/roadlog_item/${item._id}`, updatedFields);
      if (!ok) {
        console.error("Error updating item");
        setItem(originalItem);
        return;
      }

      onUpdate(data);
    } catch (error) {
      console.error(error);
      setItem(originalItem);
      onUpdate(originalItem);
    }
  };

  return (
    <div className="flex gap-4 relative overflow-hidden">
      {/* Left panel - Main settings */}
      <div className="w-3/12 bg-white p-4 shadow-md shadow-r">
        <div className="flex flex-col space-y-6">
          {/* General Section */}
          <div className="space-y-2">
            {/* Module Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Module</label>
              <SelectModule
                item={item}
                onChange={(e) => {
                  updateItem({
                    module_id: e.module_id,
                    module_name: e.module_name,
                  });
                }}
                projectId={project._id}
              />
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Budget</label>
              <SelectBudget
                value={{ name: item.budget_name, _id: item.budget_id }}
                onChange={(e) => {
                  updateItem({
                    budget_name: e?._id ? e.name : null,
                    budget_id: e?._id || null,
                  });
                }}
                projectId={item.project_id}
              />
            </div>

            {/* Assignees Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Assignees</label>
              <SelectUser2 value={item.users || []} onChange={(e) => updateItem({ users: e })} roles={["admin", "super_admin", "user", "client"]} projectId={project._id} />
            </div>

            {/* Tags Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Tags</label>
              <SelectTags value={item.roadlog_tags || []} onChange={(e) => updateItem({ roadlog_tags: e })} projectId={item.project_id} />
            </div>

            {/* Sprint Dates Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Sprint Dates</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={item.sprint_start_date ? new Date(item.sprint_start_date).toISOString().split("T")[0] : ""}
                    onChange={(e) => updateItem({ sprint_start_date: e.target.value ? new Date(e.target.value) : null })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={item.sprint_end_date ? new Date(item.sprint_end_date).toISOString().split("T")[0] : ""}
                    onChange={(e) => updateItem({ sprint_end_date: e.target.value ? new Date(e.target.value) : null })}
                    min={item.sprint_start_date ? new Date(item.sprint_start_date).toISOString().split("T")[0] : undefined}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* T-shirt Section */}
          {(!isFromBacklog || (isFromBacklog && !item.is_in_backlog)) && (
            <>
              <div className="space-y-2">
                <div className="border-b border-gray-200 pb-1">
                  <h3 className="text-sm font-medium text-gray-700">T-shirt</h3>
                </div>

                {/* Business Contribution Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Business Contribution</label>
                  <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-gray-50">
                    {["XS", "S", "M", "L", "XL"].map((size) => (
                      <button
                        key={size}
                        type="button"
                        className={`flex-1 py-2 text-xs font-medium transition-colors ${
                          item.business_contribution === size ? "bg-indigo-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => updateItem({ business_contribution: size })}>
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Charge Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Charge</label>
                  <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-gray-50">
                    {["XS", "S", "M", "L", "XL"].map((size) => (
                      <button
                        key={size}
                        type="button"
                        className={`flex-1 py-2 text-xs font-medium transition-colors ${
                          item.charge === size ? "bg-indigo-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => updateItem({ charge: size })}>
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Urgency Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Urgency</label>
                  <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-gray-50">
                    <button
                      type="button"
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${!item.urgent ? "bg-indigo-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                      onClick={() => updateItem({ urgent: false })}>
                      Normal
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${item.urgent ? "bg-red-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                      onClick={() => updateItem({ urgent: true })}>
                      Urgent
                    </button>
                  </div>
                </div>
              </div>

              {/* NNL Section */}
              <div className="space-y-1">
                <div className="border-b border-gray-200 pb-1">
                  <h3 className="text-sm font-medium text-gray-700">Now, Next, Later</h3>
                </div>

                <div className="space-y-2 pt-2">
                  <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-gray-50">
                    <button
                      type="button"
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${item.nnl === "NOW" ? "bg-indigo-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                      onClick={() => updateItem({ nnl: "NOW" })}>
                      NOW
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${
                        item.nnl === "NEXT" ? "bg-indigo-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => updateItem({ nnl: "NEXT" })}>
                      NEXT
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${
                        item.nnl === "LATER" ? "bg-indigo-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => updateItem({ nnl: "LATER" })}>
                      LATER
                    </button>
                  </div>
                </div>
                {/* Days Required */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Days required</label>
                  <Hours
                    value={item.days_required}
                    onChange={(days) => {
                      const tjm = item.tjm || 500;
                      const estimation = (days * tjm).toFixed(2);
                      updateItem({
                        days_required: days,
                        estimation,
                      });
                    }}
                  />
                </div>

                {/* Resource Profile */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Resource Profile</label>
                  <div className="relative">
                    <select
                      className="w-full appearance-none rounded-lg border border-gray-300 bg-white py-2.5 px-3 text-sm shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                      value={item.resource_profile || "EXPERIMENTED DEVELOPER"}
                      onChange={(e) => {
                        let tjm = 500;
                        if (e.target.value === "CTO") tjm = 650;
                        if (e.target.value === "IA ENGINEER") tjm = 600;
                        if (e.target.value === "EXPERIMENTED DEVELOPER") tjm = 500;
                        if (e.target.value === "DEVELOPER") tjm = 350;
                        if (e.target.value === "PRODUCT") tjm = 500;
                        if (e.target.value === "DevOps") tjm = 750;
                        if (e.target.value === "INTERN") tjm = 200;

                        const days = item.days_required || 0;
                        const estimation = days * tjm;

                        updateItem({
                          resource_profile: e.target.value,
                          tjm,
                          estimation: estimation.toFixed(2),
                        });
                      }}>
                      <option value="DEVELOPER">Developer</option>
                      <option value="EXPERIMENTED DEVELOPER">Experimented Developer</option>
                      <option value="CTO">CTO</option>
                      <option value="PRODUCT">Product</option>
                      <option value="IA ENGINEER">IA Engineer</option>
                      <option value="DevOps">DevOps</option>
                      <option value="INTERN">Intern</option>
                    </select>
                    <HiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-gray-500">TJM (€)</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full rounded-lg border border-gray-300 bg-white py-2.5 px-3 text-sm shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                      value={item.tjm || ""}
                      onChange={(e) => {
                        const customTjm = Number(e.target.value) || 0;
                        const days = item.days_required || 0;
                        const estimation = days * customTjm;
                        updateItem({
                          tjm: customTjm,
                          estimation: estimation.toFixed(2),
                        });
                      }}
                      placeholder="Enter a custom TJM"
                    />
                  </div>
                </div>

                {/* Estimated Price */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700" data-tip data-for="estimated-price">
                    Estimated price (€)
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2.5 px-3 text-sm shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:opacity-75"
                    value={item.estimation || 0}
                    disabled
                  />
                </div>
              </div>
            </>
          )}

          {/* Backlog Section */}
          {item.is_in_backlog && (
            <div className="space-y-2">
              <div className="border-b border-gray-200 pb-1">
                <h3 className="text-sm font-medium text-gray-700">Backlog</h3>
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Priority
                  <a href="https://www.selego.co/blog/hierarchiser-les-priorites-des-bugs-dans-le-developpement-agile" target="_blank" className="ml-1">
                    <IoInformationCircleOutline className="text-indigo-500" />
                  </a>
                </label>
                <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-gray-50">
                  {["P0", "P1", "P2", "P3"].map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${
                        item.priority === priority ? getPriorityColor(priority) : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => updateItem({ priority })}>
                      {priority}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Category</label>
                <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-gray-50">
                  {["BUG", "FEATURE"].map((category) => (
                    <button
                      key={category}
                      type="button"
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${
                        item.category === category ? getCategoryColor(category) : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => updateItem({ category })}>
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Status
                  <span className="ml-1 text-xs text-gray-500">(Required)</span>
                </label>
                <SelectStatus value={item.status} onChange={(status) => updateItem({ status })} />
              </div>

              {/* Days Required */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Days required</label>
                <Hours
                  value={item.days_required}
                  onChange={(days) => {
                    const tjm = item.tjm || 500;
                    const estimation = (days * tjm).toFixed(2);
                    updateItem({
                      days_required: days,
                      estimation,
                    });
                  }}
                />
              </div>

              {/* Resource Profile */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Resource Profile</label>
                <div className="relative">
                  <select
                    className="w-full appearance-none rounded-lg border border-gray-300 bg-white py-2.5 px-3 text-sm shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    value={item.resource_profile || "EXPERIMENTED DEVELOPER"}
                    onChange={(e) => {
                      let tjm = 500;
                      if (e.target.value === "CTO") tjm = 650;
                      if (e.target.value === "IA ENGINEER") tjm = 600;
                      if (e.target.value === "EXPERIMENTED DEVELOPER") tjm = 500;
                      if (e.target.value === "DEVELOPER") tjm = 350;
                      if (e.target.value === "PRODUCT") tjm = 500;
                      if (e.target.value === "DevOps") tjm = 750;
                      if (e.target.value === "INTERN") tjm = 200;

                      const days = item.days_required || 0;
                      const estimation = days * tjm;

                      updateItem({
                        resource_profile: e.target.value,
                        tjm,
                        estimation: estimation.toFixed(2),
                      });
                    }}>
                    <option value="DEVELOPER">Developer</option>
                    <option value="EXPERIMENTED DEVELOPER">Experimented Developer</option>
                    <option value="CTO">CTO</option>
                    <option value="PRODUCT">Product</option>
                    <option value="IA ENGINEER">IA Engineer</option>
                    <option value="DevOps">DevOps</option>
                    <option value="INTERN">Intern</option>
                  </select>
                  <HiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Estimated Price */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700" data-tip data-for="estimated-price">
                  Estimated price (€)
                </label>
                <input
                  type="number"
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2.5 px-3 text-sm shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:opacity-75"
                  value={item.estimation || 0}
                  disabled
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Middle section - Comments */}
      <div className="w-9/12 relative overflow-hidden">
        <div className="py-4 pr-4">
          <Comments value={item} setValue={setItem} iaResponse={iaResponse} setIaResponse={setIaResponse} />
        </div>
        <div className="mb-2 pl-4">
          <span className="flex items-center text-xs text-gray-600 italic">
            Created by&nbsp;{item.user_name} on {moment(item.created_at).format("DD/MM/YYYY")}
          </span>
        </div>

        <button
          onClick={() => setIsIaToolOpen(!isIaToolOpen)}
          className="absolute right-4 top-4 z-30 bg-indigo-500 hover:bg-indigo-600 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          title={isIaToolOpen ? "Fermer l'assistant" : "Ouvrir l'assistant"}>
          <FaRobot className="w-5 h-5" />
        </button>

        <div
          className={`absolute top-0 right-0 h-full w-full bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-20 ${
            isIaToolOpen ? "translate-x-0" : "translate-x-full"
          }`}>
          <div className="h-full">
            <IaTool onAddToComments={setIaResponse} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Hours = ({ value, onChange }) => {
  const [hours, setHours] = useState(0);

  useEffect(() => {
    setHours(value);
  }, [value]);

  return (
    <input
      type="number"
      className="w-full rounded-lg border border-gray-300 bg-white py-2.5 px-3 text-sm shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
      value={hours}
      onChange={(e) => setHours(e.target.value)}
      onBlur={() => onChange(hours)}
    />
  );
};

const SelectTags = ({ value, onChange, projectId }) => {
  const [tags, setTags] = useState(value || []);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setTags(value || []);
  }, [value]);

  useEffect(() => {
    getTags();
  }, [projectId]);

  async function getTags() {
    setIsLoading(true);
    try {
      const { ok, data } = await api.post("/roadlog_tag/search", { project_id: projectId, limit: 1000, sort: "name" });
      if (!ok) throw new Error("Failed to fetch tags");
      setOptions(data);
    } catch (error) {
      console.error("Error fetching tags:", error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateNew(newOption) {
    setIsLoading(true);
    try {
      const { ok, data } = await api.post("/roadlog_tag", {
        name: newOption.label,
        project_id: projectId,
      });
      if (!ok) throw new Error("Failed to create tag");
      const newTagsList = [...tags, { _id: data._id, name: data.name }];
      const newOptions = [...options, data];
      setTags(newTagsList);
      setOptions(newOptions);
      onChange(newTagsList);
    } catch (error) {
      console.error("Error creating tag:", error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleSelectionChange(selectedOptions) {
    const newTagsList = selectedOptions.map((opt) => ({ _id: opt.value, name: opt.label }));
    setTags(newTagsList);
    onChange(newTagsList);
  }

  return (
    <div className="flex flex-col gap-2">
      <MultiSelect
        values={tags.map((tag) => ({ value: tag._id, label: tag.name }))}
        id="select-tags"
        options={options.map((tag) => ({ value: tag._id, label: tag.name }))}
        onSelectedChange={handleSelectionChange}
        placeholder="Tags"
        buttonClassName="px-3 py-2"
        onCreateNew={handleCreateNew}
        isLoading={isLoading}
      />

      <div className="flex flex-wrap gap-1.5 mt-1">
        {tags.map((tag) => (
          <span
            key={tag._id}
            className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 cursor-pointer transition-colors flex items-center"
            onClick={() => {
              const newTags = tags.filter((t) => t !== tag);
              setTags(newTags);
              onChange(newTags);
            }}>
            {tag.name}
            <HiXMark className="ml-1 w-3 h-3" />
          </span>
        ))}
      </div>
    </div>
  );
};

const SelectStatus = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const statusId = "status-select";

  const statusOptions = [
    { value: "TODO", label: "TODO" },
    { value: "READY_TO_DEV", label: "READY TO DEV" },
    { value: "IN_PROGRESS", label: "IN PROGRESS" },
    { value: "TESTING", label: "TESTING" },
    { value: "DONE", label: "DONE" },
    { value: "ARCHIVED", label: "ARCHIVED" },
  ];

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const idRegex = new RegExp(statusId);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !idRegex.test(event.target.id)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  return (
    <div className="relative w-full">
      <button
        id={statusId}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex w-full justify-between items-center gap-4 border border-gray-200 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 px-3 py-2">
        <span className="flex-1 text-left">{value || "Select status"}</span>
        {isOpen ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="absolute z-20 bottom-full mb-1 w-full bg-white shadow-lg rounded-md overflow-auto border border-gray-200">
          <ul className="py-1">
            {statusOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`flex items-center px-3 py-2 text-sm cursor-pointer ${value === option.value ? "bg-blue-50 text-blue-700 font-medium" : "hover:bg-gray-50"}`}>
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const SelectModule = ({ item, onChange, projectId }) => {
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const moduleId = "module-select";

  // Get module options from API
  useEffect(() => {
    getModules();
  }, []);

  async function getModules() {
    try {
      const { ok, data } = await api.post(`/roadlog_module/search`, { project_id: projectId });
      if (!ok) return;
      setOptions(data);
    } catch (error) {
      console.error(error);
    }
  }

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const idRegex = new RegExp(moduleId);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !idRegex.test(event.target.id)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Create a new module
  async function handleCreateNew() {
    try {
      const { ok, data } = await api.post("/roadlog_module", {
        title: search,
        project_id: projectId,
      });

      if (ok && data) {
        onChange({
          module_id: data._id,
          module_name: data.title,
        });
        await getModules();
        setIsOpen(false);
        setSearch("");
      }
    } catch (error) {
      console.error("Error creating module:", error);
    }
  }

  // Clear selection
  function handleClear(e) {
    e.stopPropagation();
    onChange({
      module_id: null,
      module_name: null,
    });
  }

  // Filter options based on search
  const filteredOptions = options.filter((mod) => {
    if (!search) return true;
    return mod.title.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="relative w-full">
      <button
        id={moduleId}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex w-full justify-between items-center gap-4 border border-gray-200 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 px-3 py-2">
        <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">{item?.module_name || "Select a module"}</span>
        <div className="flex items-center gap-1">
          {item?.module_name && (
            <button onClick={handleClear} className="p-0.5 hover:bg-gray-100 rounded-full">
              <HiXMark className="w-4 h-4 text-gray-500" />
            </button>
          )}
          {isOpen ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="absolute z-20 mt-1 w-full bg-white shadow-lg rounded-md overflow-auto border border-gray-200 max-h-72">
          {/* Search bar */}
          <div className="sticky top-0 bg-white p-1.5 border-b border-gray-200">
            <div className="relative w-full">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <HiMagnifyingGlass className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                ref={searchInputRef}
                className="block w-full rounded-md border border-gray-300 pl-10 pr-2 py-1.5 text-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search or create new module"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && search) {
                    if (!filteredOptions.find((o) => o.title.toLowerCase() === search.toLowerCase())) {
                      handleCreateNew();
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Options list */}
          <ul className="max-h-40 overflow-y-auto py-1">
            {filteredOptions.map((option) => {
              const isSelected = item?.module_id === option._id;
              return (
                <li
                  key={option._id}
                  onClick={() => {
                    onChange({
                      module_id: option._id,
                      module_name: option.title,
                    });
                    setIsOpen(false);
                  }}
                  className={`flex items-center px-2 py-1.5 text-sm cursor-pointer ${isSelected ? "bg-indigo-50 text-indigo-700" : "hover:bg-gray-100"}`}>
                  <span className={`flex-1 ${isSelected ? "font-medium" : "font-normal"}`}>{option.title}</span>
                </li>
              );
            })}

            {/* Create option */}
            {search && !filteredOptions.find((o) => o.title.toLowerCase() === search.toLowerCase()) && (
              <li onClick={handleCreateNew} className="flex items-center gap-1.5 px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100">
                <HiPlus className="text-gray-500 w-4 h-4" />
                <span className="flex-1 font-medium text-indigo-600">Create module "{search}"</span>
              </li>
            )}

            {filteredOptions.length === 0 && !search && <li className="px-2 py-1.5 text-sm text-gray-500 italic">No modules found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case "P0":
      return "bg-red-100 text-red-800 border-red-300";
    case "P1":
      return "bg-orange-100 text-orange-800 border-orange-300";
    case "P2":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "P3":
      return "bg-green-100 text-green-800 border-green-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "TODO":
      return "bg-blue-100 text-blue-800 border-blue-300";
    case "READY_TO_DEV":
      return "bg-purple-100 text-purple-800 border-purple-300";
    case "IN_PROGRESS":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "TESTING":
      return "bg-orange-100 text-orange-800 border-orange-300";
    case "DONE":
      return "bg-green-100 text-green-800 border-green-300";
    case "ARCHIVED":
      return "bg-gray-100 text-gray-800 border-gray-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

const getCategoryColor = (category) => {
  switch (category) {
    case "BUG":
      return "bg-red-100 text-red-800 border-red-300";
    case "FEATURE":
      return "bg-blue-100 text-blue-800 border-blue-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

const SelectBudget = ({ value, onChange, projectId, status = "active" }) => {
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const budgetId = "budget-select";

  // Get budget options from API
  useEffect(() => {
    if (projectId) getBudgets();
  }, [projectId]);

  async function getBudgets() {
    try {
      const query = { limit: 20, status };
      if (projectId) query.projectId = projectId;
      const { data } = await api.post("/budget/search", query);
      setOptions(data.sort((a, b) => (a.name || "").localeCompare(b.name)));
    } catch (error) {
      console.error("Error fetching budgets:", error);
    }
  }

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const idRegex = new RegExp(budgetId);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !idRegex.test(event.target.id)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Clear selection
  function handleClear(e) {
    e.stopPropagation();
    onChange(null);
  }

  return (
    <div className="relative w-full">
      <button
        id={budgetId}
        type="button"
        disabled={!projectId}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex w-full justify-between items-center gap-4 border border-gray-200 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 disabled:opacity-60 disabled:cursor-not-allowed">
        <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">{value?.name || "Select budget"}</span>
        <div className="flex items-center gap-1">
          {value?.name && (
            <button onClick={handleClear} className="p-0.5 hover:bg-gray-100 rounded-full">
              <HiXMark className="w-4 h-4 text-gray-500" />
            </button>
          )}
          {isOpen ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="absolute z-20 mt-1 w-full bg-white shadow-lg rounded-md overflow-auto border border-gray-200 max-h-72">
          <ul className="max-h-40 overflow-y-auto py-1">
            {options.map((option) => {
              const isSelected = value?._id === option._id;
              return (
                <li
                  key={option._id}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={`flex items-center px-2 py-1.5 text-sm cursor-pointer ${isSelected ? "bg-indigo-50 text-indigo-700" : "hover:bg-gray-100"}`}>
                  <span className={`flex-1 ${isSelected ? "font-medium" : "font-normal"}`}>{option.name}</span>
                </li>
              );
            })}
            {options.length === 0 && <li className="px-2 py-1.5 text-sm text-gray-500 italic">No budgets found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Info;
